import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Select } from "antd";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { useEffect, useMemo, useState } from "react";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { getAllStudentByYear } from "../../../api/etudiants";
import { getStudentPaiementPlans } from "../../../api/studentPaiementPlan";
import { updateDiscount } from "../../../api/bourse_remise";
import withPermission from "../../../core/common/autorisations/withPermission";

type Props = {
  show: boolean;
  onClose: () => void;
  onSaved?: () => void;
  currency?: string;
  record?: {
    discount_id: string;
    corresponding: string;
    number_corresponding?: string;
    description?: string;
    correspondant?: string;
    type: "Bourse" | "Reduction";
    discount_amount: number;
    student_id: string;
    firstname?: string;
    lastname?: string;
    repartitions?: Array<{
      repartition_id: string;
      repartition_amount: number;
      student_repartition_id: string;
      payment_plan: {
        id: string;
        label: string;
        amount: number;
        due_date?: string;
      };
    }>;
  } | null;
  lockStudent?: boolean;
};

enum DiscountType {
  BOURSE = "Bourse",
  REMISE = "Reduction",
}

const schema = z.object({
  description: z.string().optional(),
  correspondant: z.string().optional(),
  number_corresponding: z.string().optional(),
  type: z.enum([DiscountType.BOURSE, DiscountType.REMISE]),
  id_studentProfil: z.string().nonempty(),
  ids_paiement_plan: z.array(z.string()).min(1),
  amount: z.number().nonnegative(),
});

type FormFields = z.infer<typeof schema>;

const EditBourseRemiseModal = ({
  show,
  onClose,
  onSaved,
  record,
  lockStudent,
  currency = "FCFA",
}: Props) => {
  const getModalContainer = () => {
    const modalElement = document.getElementById("edit_bourse_remise_modal");
    return modalElement ? modalElement : document.body;
  };

  const { selectedYear } = useAcademicYear();

  const [students, setStudents] = useState<
    {
      id: string;
      lastname: string;
      firstname: string;
      levelAbbr?: string;
      classAbbr?: string;
    }[]
  >([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [paiementPlans, setPaiementPlans] = useState<any[]>([]);
  const [planAllocations, setPlanAllocations] = useState<
    Record<string, number>
  >({});
  const [discountMode, setDiscountMode] = useState<"AMOUNT" | "PERCENTAGE">(
    "AMOUNT",
  );
  const [percentage, setPercentage] = useState<number>(0);

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(schema),
    defaultValues: {
      type: record?.type || DiscountType.BOURSE,
      description: record?.description || "",
      correspondant: record?.correspondant || "",
      number_corresponding: record?.number_corresponding || "",
      amount: record?.discount_amount || 0,
      id_studentProfil: record?.student_id || "",
      ids_paiement_plan: (record?.repartitions || []).map(
        (r) => r.payment_plan.id,
      ),
    } as any,
  });

  useEffect(() => {
    if (!show || !record) return;
    reset({
      type: record?.type || DiscountType.BOURSE,
      description: record?.description || "",
      correspondant: record?.corresponding || "",
      number_corresponding: record?.number_corresponding || "",
      amount: record?.discount_amount || 0,
      id_studentProfil: record?.student_id || "",
      ids_paiement_plan: (record?.repartitions || []).map(
        (r) => r.payment_plan.id,
      ),
    } as any);
    const initialAlloc: Record<string, number> = {};
    (record?.repartitions || []).forEach((r) => {
      initialAlloc[r.payment_plan.id] = r.repartition_amount || 0;
    });
    setPlanAllocations(initialAlloc);
  }, [show, record, reset]);

  const id_paymentPlans = watch("ids_paiement_plan") || [];
  const totalAmount = watch("amount") || 0;

  const selectedPlans = paiementPlans.filter((plan) =>
    id_paymentPlans.includes(plan.id_paymentPlans),
  );

  const selectedPlansRemaining = useMemo(
    () =>
      selectedPlans.reduce(
        (acc, plan) =>
          acc +
          Math.max(
            0,
            Number(plan.PaymentPlan?.amount || 0) -
              Number(plan.paid || 0) -
              Number(plan.discount || 0),
          ),
        0,
      ),
    [selectedPlans],
  );

  const selectedType = watch("type");
  const watchedAmount = watch("amount") || 0;

  useEffect(() => {
    if (selectedType === DiscountType.BOURSE) {
      setDiscountMode("PERCENTAGE");
      setPercentage(100);
    }
  }, [selectedType]);

  // Initialize percentage when record and selectedPlansRemaining are available
  useEffect(() => {
    if (!record || selectedPlansRemaining === 0) return;
    
    const initialAmount = record?.discount_amount || 0;
    const calculatedPercentage = initialAmount > 0
      ? Math.round((initialAmount / selectedPlansRemaining) * 100)
      : 0;
    setPercentage(calculatedPercentage);
  }, [record?.discount_amount, selectedPlansRemaining]);

  useEffect(() => {
    if (discountMode === "PERCENTAGE") {
      if (
        selectedType !== DiscountType.BOURSE &&
        percentage === 0 &&
        watchedAmount > 0 &&
        selectedPlansRemaining > 0
      ) {
        setPercentage(
          Math.min(
            100,
            Math.round((watchedAmount / selectedPlansRemaining) * 100),
          ),
        );
        return;
      }

      const amountValue = Math.round(
        (selectedPlansRemaining * percentage) / 100,
      );
      setValue("amount", amountValue, { shouldValidate: true });
      return;
    }

    const percentValue = selectedPlansRemaining
      ? Math.round((watchedAmount / selectedPlansRemaining) * 100)
      : 0;
    setPercentage(percentValue);
  }, [
    discountMode,
    percentage,
    selectedPlansRemaining,
    setValue,
    watchedAmount,
    selectedType,
  ]);

  const initStudents = async () => {
    try {
      if (!selectedYear?.id) return;
      const response = await getAllStudentByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.BOURSE_REMISE, Action.UPDATE),
      );
      const raw = response.data || [];
      const formatted = (raw || [])
        .map((s: any) => {
          const id = String(s?.StudentProfil?.id ?? s?.id ?? "");
          const firstname = s?.StudentProfil?.firstname ?? s?.firstname ?? "";
          const lastname = s?.StudentProfil?.lastname ?? s?.lastname ?? "";
          const levelAbbr =
            s?.Registrations?.[0]?.Class?.Level?.abbreviation ??
            s?.Level?.abbreviation ??
            "";
          const classAbbr =
            s?.Registrations?.[0]?.Class?.abbreviation ??
            s?.Class?.abbreviation ??
            "";
          return { id, firstname, lastname, levelAbbr, classAbbr };
        })
        .sort((a: any, b: any) => {
          const levelCmp = (a.levelAbbr || "").localeCompare(b.levelAbbr || "");
          return levelCmp !== 0
            ? levelCmp
            : (a.classAbbr || "").localeCompare(b.classAbbr || "");
        });
      setStudents(formatted);
      setValue(
        "id_studentProfil",
        record?.student_id || formatted[0]?.id || "",
      );
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erreur lors du chargement des étudiants",
      );
    }
  };

  useEffect(() => {
    if (show) initStudents();
  }, [show, selectedYear, record]);

  const selectedStudentId = watch("id_studentProfil") || record?.student_id;
  const selectedStudent: any =
    students.find((s: any) => s.id === selectedStudentId) ??
    (selectedStudentId ? { id: selectedStudentId } : undefined);

  // fonction pour charger les plans
  const loadPlans = async () => {
    if (!selectedStudent?.id || !selectedYear?.id) return;
    setLoadingPlans(true);
    try {
      const response = await getStudentPaiementPlans(
        selectedStudent.id,
        selectedYear.id,
        buildPermissionContext(Resource.BOURSE_REMISE, Action.UPDATE),
      );

      const plans = response.data?.map((p: any) => p) || [];

      setPaiementPlans(plans);
      if (record) {
        const initialSelected = (record?.repartitions || []).map(
          (r) => r.payment_plan?.id || "",
        );
        setValue("ids_paiement_plan", initialSelected);
        const initialAlloc: Record<string, number> = {};
        (record?.repartitions || []).forEach((r) => {
          initialAlloc[r.payment_plan?.id || ""] = r.repartition_amount || 0;
        });
        setPlanAllocations(initialAlloc);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erreur lors du chargement des plans de paiement de l'étudiant",
      );
    } finally {
      setLoadingPlans(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, [selectedStudent, selectedYear, show, record]);

  useEffect(() => {
    setPlanAllocations((prev) => {
      const next: Record<string, number> = {};
      id_paymentPlans.forEach((pid: string) => {
        next[pid] = prev[pid] || 0;
      });
      return next;
    });
  }, [id_paymentPlans]);

  const parseDate = (d?: string) => {
    if (!d) return new Date();
    const parts = d.split(".");
    if (parts.length !== 3) return new Date();
    return new Date(
      parseInt(parts[2], 10),
      parseInt(parts[1], 10) - 1,
      parseInt(parts[0], 10),
    );
  };

  const autoDistribute = (
    totalAmount: number,
    selectedPlans: any[],
  ) => {
    let remaining = totalAmount;

    const sortedPlans = [...selectedPlans].sort(
      (a, b) =>
        parseDate(a.PaymentPlan?.due_date).getTime() -
        parseDate(b.PaymentPlan?.due_date).getTime(),
    );

    const allocations: Record<string, number> = {};

    for (const plan of sortedPlans) {
      const remainingPlan = Math.max(
        0,
        Number(plan.PaymentPlan?.amount || 0) -
          Number(plan.paid || 0) -
          Number(plan.discount || 0),
      );

      const allocated = Math.min(remaining, remainingPlan);

      allocations[plan.id_paymentPlans] = allocated;

      remaining -= allocated;

      if (remaining <= 0) break;
    }

    return allocations;
  };

  const handleAutoDistribute = () => {
    const plans = paiementPlans.filter((plan) =>
      id_paymentPlans.includes(plan.id_paymentPlans),
    );

    setPlanAllocations(autoDistribute(totalAmount, plans));
  };

  const totalAllocated = useMemo(
    () => Object.values(planAllocations).reduce((s, v) => s + (v || 0), 0),
    [planAllocations],
  );

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      if (!record?.discount_id) {
        toast.error("Impossible de modifier: bourse/remise introuvable");
        return;
      }
      if (totalAllocated > (data.amount || 0)) {
        toast.error(
          "La somme des montants par plan dépasse le montant de la bourse/remise",
        );
        return;
      }
      const payload = {
        id_studentProfil: data.id_studentProfil,
        amount: data.amount,
        type: data.type,
        description: data.description,
        corresponding: data.correspondant,
        number_corresponding: data.number_corresponding,
        selectedPlans: (data.ids_paiement_plan || []).map((pid: string) => ({
          id: paiementPlans.find((p) => p.id_paymentPlans === pid)?.id || "",
          amount: planAllocations[pid] ?? 0,
        })),
      };
      await updateDiscount(
        record?.discount_id,
        payload,
        buildPermissionContext(Resource.BOURSE_REMISE, Action.UPDATE),
      );
      reset();
      onSaved?.();
      toast.success("Bourse / Remise modifiée !");
      onClose();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Une erreur s'est produite, Réessayez plus tard",
      );
    }
  };

  return (
    <>
      <Modal show={show} onHide={() => onClose()} size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>Modifier Bourse / Remise</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white" id="edit_bourse_remise_modal">
          {errors.root && (
            <div className="alert alert-danger rounded-pill alert-dismissible fade show">
              {errors.root.message}
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => clearErrors("root")}
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
          )}
          <form
            id="edit-discount-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row g-3">
              <div className="col-12">
                <div className="card shadow-sm border-dark rounded">
                  <div className="card-header bg-light d-flex align-items-center justify-content-between">
                    <h6 className="mb-0">Informations</h6>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label">Étudiant :</label>
                        <br />
                        {lockStudent ? (
                          <>
                            <span>
                              {selectedStudent?.levelAbbr
                                ? `${selectedStudent?.levelAbbr} - ${selectedStudent?.classAbbr} `
                                : ""}
                              {selectedStudent?.lastname}{" "}
                              {selectedStudent?.firstname}
                            </span>
                          </>
                        ) : (
                          <Select
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toString()
                                .toLowerCase()
                                .includes(input.toString().toLowerCase())
                            }
                            size="large"
                            style={{ width: "100%" }}
                            getPopupContainer={getModalContainer}
                            placeholder="Sélectionner un étudiant"
                            disabled={lockStudent}
                            value={watch("id_studentProfil") || undefined}
                            onChange={(value) =>
                              setValue("id_studentProfil", value as string, {
                                shouldValidate: true,
                              })
                            }
                            status={
                              errors.id_studentProfil ? "error" : undefined
                            }
                            options={students.map((s) => ({
                              label: `${s.levelAbbr ? `${s.levelAbbr} - ${s.classAbbr} ` : ""}${s.lastname} ${s.firstname}`,
                              value: s.id,
                            }))}
                            className={
                              errors.id_studentProfil
                                ? "ant-select-status-error"
                                : undefined
                            }
                          />
                        )}
                        {errors.id_studentProfil && (
                          <div className="invalid-feedback">
                            {errors.id_studentProfil.message}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">
                          Correspondant (optionnel)
                        </label>
                        <input
                          {...register("correspondant")}
                          type="text"
                          className="form-control"
                          placeholder="Ex: Parent, tuteur, sponsor..."
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">
                          Numéro Correspondant (optionnel)
                        </label>
                        <input
                          {...register("number_corresponding")}
                          type="text"
                          className="form-control"
                          placeholder="Ex: +223 602037404"
                        />
                      </div>
                      <div className="d-flex flex-column justify-content-between col-12 col-md-6">
                        <div className="col-12 col-md-4">
                          <label className="form-label">Type</label>
                          <div className="form-switch">
                            <input
                              className={
                                errors.type
                                  ? "form-check-input is-invalid"
                                  : "form-check-input"
                              }
                              type="checkbox"
                              role="switch"
                              id="typeSwitchEdit"
                              onChange={() => {
                                setValue(
                                  "type",
                                  watch("type") === DiscountType.REMISE
                                    ? DiscountType.BOURSE
                                    : DiscountType.REMISE,
                                );
                              }}
                              checked={watch("type") === DiscountType.REMISE}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="typeSwitchEdit"
                            >
                              {watch("type") === DiscountType.REMISE
                                ? " Remise"
                                : " Bourse"}
                            </label>
                          </div>
                          {errors.type && (
                            <div className="invalid-feedback">
                              {errors.type.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Description</label>
                        <textarea
                          {...register("description")}
                          className={
                            errors.description
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          rows={3}
                          placeholder="Ex: Bourse attribuée aux étudiants méritants"
                        />
                        {errors.description && (
                          <div className="invalid-feedback">
                            {errors.description.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div className="card shadow-sm border-dark rounded">
                  <div className="card-header bg-light d-flex align-items-center justify-content-between">
                    <h6 className="mb-0">Plans de paiement</h6>
                    <div className="dropdown">
                      <button
                        className="btn btn-outline-light bg-white dropdown-toggle"
                        data-bs-toggle="dropdown"
                        style={{ minWidth: "220px" }}
                      >
                        <i className="ti ti-wallet me-2" />
                        {id_paymentPlans?.length > 0
                          ? `${id_paymentPlans.length} tranche(s)`
                          : "Plans de paiement"}
                      </button>
                      <ul
                        className="dropdown-menu p-2"
                        onClick={(e) => e.stopPropagation()}
                        style={{ minWidth: "240px" }}
                      >
                        {paiementPlans.map((plan) => (
                          <li key={plan.id_paymentPlans} className="mb-2">
                            <label
                              className={`dropdown-item d-flex flex-column ${
                                id_paymentPlans?.includes(plan.id_paymentPlans)
                                  ? "bg-light text-primary rounded-1"
                                  : ""
                              }`}
                              style={{ cursor: "pointer" }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input m-0"
                                  checked={id_paymentPlans?.includes(
                                    plan.id_paymentPlans,
                                  )}
                                  onChange={() => {
                                    if (
                                      id_paymentPlans?.includes(
                                        plan.id_paymentPlans,
                                      )
                                    ) {
                                      setValue(
                                        "ids_paiement_plan",
                                        id_paymentPlans?.filter(
                                          (id) => id !== plan.id_paymentPlans,
                                        ),
                                      );
                                    } else {
                                      setValue("ids_paiement_plan", [
                                        ...id_paymentPlans,
                                        plan.id_paymentPlans,
                                      ]);
                                    }
                                  }}
                                />
                                <span>{plan?.PaymentPlan?.label}</span>
                              </div>
                              <div className="ms-4">
                                <small className="text-muted">
                                  {plan?.PaymentPlan?.amount?.toLocaleString(
                                    "fr-FR",
                                  )}{" "}
                                  {currency}
                                </small>
                              </div>
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <button
                      type="button"
                      className="btn btn-info btn-sm"
                      onClick={handleAutoDistribute}
                      disabled={!id_paymentPlans?.length || totalAmount <= 0}
                    >
                      Auto-répartition
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="d-flex align-items-start gap-3">
                      {loadingPlans ? (
                        <span className="text-center w-100 h-100 fs-16">
                          Chargement des plans...
                        </span>
                      ) : (
                        <div className="flex-grow-1">
                          {id_paymentPlans?.length > 0 ? (
                            <>
                              <div className="d-flex flex-column gap-2">
                                {paiementPlans
                                  .filter((p) =>
                                    id_paymentPlans.includes(p.id_paymentPlans),
                                  )
                                  .map((plan) => {
                                    const maxAmount =
                                      plan?.PaymentPlan?.amount ?? undefined;
                                    const pid = plan.id_paymentPlans;
                                    const val = planAllocations[pid] ?? 0;
                                    return (
                                      <div
                                        key={pid}
                                        className="d-flex align-items-center justify-content-between border rounded-2 p-2"
                                      >
                                        <div className="d-flex flex-column">
                                          <span className="fw-semibold">
                                            {plan?.PaymentPlan?.label}
                                          </span>
                                          <small className="text-muted">
                                            {maxAmount?.toLocaleString("fr-FR")}{" "}
                                            {currency}
                                          </small>
                                        </div>
                                        <div
                                          className="input-group"
                                          style={{ width: 220 }}
                                        >
                                          <input
                                            type="number"
                                            className="form-control"
                                            min={0}
                                            max={maxAmount}
                                            value={val}
                                            onChange={(e) => {
                                              const num = parseFloat(
                                                e.target.value || "0",
                                              );
                                              setPlanAllocations((prev) => ({
                                                ...prev,
                                                [pid]: Math.max(
                                                  0,
                                                  isNaN(num) ? 0 : num,
                                                ),
                                              }));
                                            }}
                                          />
                                          <span className="input-group-text bg-info-subtle text-info">
                                            {currency}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                              <div>
                                <label className="form-label">
                                  Montant / Pourcentage
                                </label>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                  {discountMode === "AMOUNT" ? (
                                    <div className="input-group mb-0" style={{ flex: 1 }}>
                                      <input
                                        {...register("amount", { valueAsNumber: true })}
                                        type="number"
                                        min={0}
                                        className={
                                          errors.amount
                                            ? "form-control is-invalid"
                                            : "form-control"
                                        }
                                        placeholder="Ex: 150 000 FCFA"
                                      />
                                      <span className="input-group-text">{currency}</span>
                                    </div>
                                  ) : (
                                    <div className="input-group mb-0" style={{ flex: 1 }}>
                                      <input
                                        type="number"
                                        className="form-control"
                                        min={0}
                                        max={100}
                                        value={percentage}
                                        onChange={(e) => {
                                          const value = Math.max(
                                            0,
                                            Math.min(100, parseFloat(e.target.value || "0")),
                                          );
                                          setPercentage(value);
                                        }}
                                      />
                                      <span className="input-group-text">%</span>
                                    </div>
                                  )}
                                  <div className="form-switch mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      id="discountModeSwitchEdit"
                                      checked={discountMode === "PERCENTAGE"}
                                      onChange={() =>
                                        setDiscountMode(
                                          discountMode === "AMOUNT"
                                            ? "PERCENTAGE"
                                            : "AMOUNT",
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor="discountModeSwitchEdit"
                                    >
                                      {discountMode === "PERCENTAGE"
                                        ? "Pourcentage"
                                        : "Montant"}
                                    </label>
                                  </div>
                                </div>
                                <div className="form-text mb-2">
                                  {discountMode === "AMOUNT" ? (
                                    selectedPlansRemaining > 0 ? (
                                      <>Ce montant correspond à {Math.round((watchedAmount / selectedPlansRemaining) * 100)}% du total sélectionné</>
                                    ) : (
                                      <>Sélectionnez des plans pour calculer le pourcentage</>
                                    )
                                  ) : selectedPlansRemaining > 0 ? (
                                    <>Ce pourcentage correspond à {Math.round((selectedPlansRemaining * percentage) / 100).toLocaleString("fr-FR")} {currency}</>
                                  ) : (
                                    <>Sélectionnez des plans pour calculer le montant</>
                                  )}
                                </div>
                              </div>
                              <div className="d-flex align-items-center justify-content-end gap-3 mt-2">
                                <span className="badge bg-warning-subtle text-dark">
                                  Montant total (plans):{" "}
                                  {selectedPlansRemaining.toLocaleString("fr-FR")} {currency}
                                </span>
                                <span className="badge bg-primary-subtle text-dark">
                                  Total couvert:{" "}
                                  {totalAllocated.toLocaleString("fr-FR")}{" "}
                                  {currency}
                                </span>
                                <span
                                  className={`badge ${
                                    totalAllocated > totalAmount
                                      ? "bg-danger-subtle text-dark"
                                      : "bg-success-subtle text-dark"
                                  }`}
                                >
                                  Reste (Bourse):{" "}
                                  {Math.max(
                                    0,
                                    totalAmount - totalAllocated,
                                  ).toLocaleString("fr-FR")}{" "}
                                  {currency}
                                </span>
                              </div>
                            </>
                          ) : (
                            <span className="text-muted fs-3">
                              Aucun plan sélectionné
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>
          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="edit-discount-form"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditBourseRemiseModal,
  [buildPermission(Resource.BOURSE_REMISE, Action.UPDATE)],
  "masquer",
);
